import { FunctionComponent, useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { Room, RoomUnit } from '@spike/room-model';
import styles from './SelectRoomServices.module.scss';
import ClickAwayListener from 'components/CreateBooking/UI/ClickAwayListener';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { faPaw } from '@fortawesome/pro-light-svg-icons';
import { getCapacityInfo } from '../../Utils/GetCapacityInfo';
import { getWeightRangeInfo } from '../../Utils/GetWeightRangeInfo';
import { useDispatch } from 'react-redux';
import { showWarningThunk } from '@spike/notifications-action';
import { compareDaycareFirst, getRoomAreaLabel } from 'utils/roomEligibility';

const SIZE_MISMATCH_WARNING_TEXT = 'This room does not allow pets of this size.';

const isBySizeInvalidUnit = (unit: RoomUnit): boolean => {
    const eligibility = unit.sizeEligibility;
    return eligibility === 'mismatch' || eligibility === 'inconsistent';
};

const isBySizeMatchUnit = (unit: RoomUnit): boolean => {
    const eligibility = unit.sizeEligibility;
    return eligibility === 'empty_match' || eligibility === 'active_match';
};

const hasBySizeEligibility = (units: Array<RoomUnit>): boolean =>
    units.some(unit => unit.sizeEligibility !== null && unit.sizeEligibility !== undefined);

const summarizeDaycareRoomBySizeEligibility = ({ units }: { units: Array<RoomUnit> }) => {
    if (!hasBySizeEligibility(units)) {
        return {
            hasBySizeRules: false,
            hasEligibleUnits: true,
            hasMismatchOrInconsistentUnits: false
        };
    }

    return {
        hasBySizeRules: true,
        hasEligibleUnits: units.some(unit => !isBySizeInvalidUnit(unit)),
        hasMismatchOrInconsistentUnits: units.some(unit => isBySizeInvalidUnit(unit))
    };
};

const shouldUseWarningIcon = (roomUnits?: string): boolean =>
    roomUnits === 'By Weight' || roomUnits === 'By Size' || roomUnits === 'Unavailable';

export interface SelectRoomServicesProps {
    id?: string;
    label?: string | JSX.Element;
    name?: string;
    selectedOption?: Room;
    petWeight?: number;
    placeholder?: string;
    options: Array<Room>;
    disabled?: boolean;
    className?: string;
    required?: boolean;
    information?: string;
    onSelect: (option: Room, data: boolean) => void;
}

export const SelectRoomServices: FunctionComponent<SelectRoomServicesProps> = props => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const dispatch = useDispatch();

    const sortedOptions = useMemo(() => [...props.options].sort(compareDaycareFirst), [props.options]);

    const changeHandler = (idSelected: number) => {
        const optionSelected = props.options.find(option => option.id === idSelected)!;

        const { type } = getCapacityInfo(optionSelected);

        if (type === 'weightRange') {
            const range = getWeightRangeInfo(optionSelected);

            if (
                !props.petWeight ||
                (range &&
                    ((range.min != null && props.petWeight < range.min) ||
                        (range.max != null && props.petWeight > range.max)))
            ) {
                dispatch(showWarningThunk('This room does not allow pets of this weight.'));
            }
        }

        props.onSelect(optionSelected, false);
    };

    const getRoomUnits = (room: Room) => {
        if (!room?.units || room.units.length === 0) {
            return '0/0';
        }

        const activeUnits = room.units.filter(unit => unit.active !== false);

        if (activeUnits.length === 0) {
            return '0/0';
        }

        const bySizeSummary = summarizeDaycareRoomBySizeEligibility({
            units: activeUnits
        });

        if (bySizeSummary.hasBySizeRules) {
            if (!bySizeSummary.hasEligibleUnits && bySizeSummary.hasMismatchOrInconsistentUnits) {
                return 'By Size';
            }

            const availableUnits = activeUnits.filter(unit => unit.available && isBySizeMatchUnit(unit)).length;

            return `${availableUnits}/${activeUnits.length}`;
        }

        const totalUnits = activeUnits.length;
        const { type, max } = getCapacityInfo(room);

        if (type === 'weightRange') {
            const range = getWeightRangeInfo(room);

            if (
                !props.petWeight ||
                (range &&
                    ((range.min != null && props.petWeight < range.min) ||
                        (range.max != null && props.petWeight > range.max)))
            ) {
                return 'By Weight';
            }
        }

        if (!max) {
            return `${totalUnits}/${totalUnits}`;
        }

        const availableUnits = activeUnits.filter(unit => max - (unit.occupancy ?? 0) > 0).length;

        return `${availableUnits}/${totalUnits}`;
    };

    const isRoomBySizeMismatch = (room?: Room): boolean => {
        if (!room?.units || room.units.length === 0) {
            return false;
        }

        const activeUnits = room.units.filter(unit => unit.active !== false);

        if (activeUnits.length === 0) {
            return false;
        }

        const bySizeSummary = summarizeDaycareRoomBySizeEligibility({
            units: activeUnits
        });

        return (
            bySizeSummary.hasBySizeRules &&
            !bySizeSummary.hasEligibleUnits &&
            bySizeSummary.hasMismatchOrInconsistentUnits
        );
    };

    const isRoomSelectedFull = (room?: Room) => {
        if (!room?.units || room.units.length === 0) return false;

        const activeUnits = room.units.filter(unit => unit.active !== false);
        if (activeUnits.length === 0) return false;

        const bySizeSummary = summarizeDaycareRoomBySizeEligibility({
            units: activeUnits
        });

        if (bySizeSummary.hasBySizeRules) {
            const eligibleUnits = activeUnits.filter(unit => !isBySizeInvalidUnit(unit));

            if (!bySizeSummary.hasEligibleUnits && bySizeSummary.hasMismatchOrInconsistentUnits) {
                return false;
            }

            return eligibleUnits.length > 0 && !eligibleUnits.some(unit => unit.available);
        }

        const { max } = getCapacityInfo(room);
        if (!max) return false;

        return activeUnits.every(unit => (unit.occupancy ?? 0) >= max);
    };

    const header = (
        <div className={styles.headerContainer}>
            <div className={styles.textContainer}>
                <span
                    className={clsx(styles.header, {
                        [styles.placeholder]: !props.selectedOption
                    })}
                >
                    {props.selectedOption?.name ? (
                        <>
                            <div className={clsx(styles.roomNameTypeContainer, styles.roomNameTypeContainerSelected)}>
                                <span className={clsx(styles.item, styles.sectionRoom, styles.roomName)}>
                                    {props.selectedOption?.name}
                                </span>
                                <span className={clsx(styles.item, styles.sectionRoom, styles.roomType)}>
                                    {getRoomAreaLabel(props.selectedOption)}
                                </span>
                            </div>
                            <div className={styles.roomUnits}>
                                {isRoomSelectedFull(props.selectedOption) && (
                                    <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        className={styles.exclamationTriangle}
                                    />
                                )}
                                {(() => {
                                    const roomUnits = props.selectedOption
                                        ? getRoomUnits(props.selectedOption)
                                        : undefined;

                                    return (
                                        <div className={styles.roomUnitsContainer}>
                                            <FontAwesomeIcon
                                                icon={shouldUseWarningIcon(roomUnits) ? faExclamationTriangle : faPaw}
                                                color={shouldUseWarningIcon(roomUnits) ? '#000000' : undefined}
                                            />
                                            <span className={clsx(styles.item, styles.sectionRoom, styles.units)}>
                                                {roomUnits}
                                            </span>
                                        </div>
                                    );
                                })()}
                            </div>
                        </>
                    ) : (
                        props.placeholder
                    )}
                </span>
            </div>
            <div className={styles.chevronContainer}>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    color={isCollapsed ? '#7A7A7A' : '#222222'}
                    className={styles.chevron}
                />
            </div>
        </div>
    );

    const list = (
        <div className={styles.listContainer}>
            {sortedOptions.map((opt, index) => {
                const roomUnits = getRoomUnits(opt);

                return (
                    <div
                        className={styles.itemContainer}
                        key={opt.id}
                        id={`booking_option_room_${index}`}
                        onClick={() => changeHandler(opt.id!)}
                    >
                        <div className={styles.itemContent}>
                            <div className={clsx(styles.roomItemContainer, styles.roomNameContainer)}>
                                <div className={styles.roomNameTypeContainer}>
                                    <span className={clsx(styles.item, styles.sectionRoom, styles.roomName)}>
                                        {opt.name}
                                    </span>
                                    <span className={clsx(styles.item, styles.sectionRoom, styles.roomType)}>
                                        {getRoomAreaLabel(opt)}
                                    </span>
                                </div>

                                <div className={styles.roomUnitsContainer}>
                                    <FontAwesomeIcon
                                        icon={shouldUseWarningIcon(roomUnits) ? faExclamationTriangle : faPaw}
                                        color={shouldUseWarningIcon(roomUnits) ? '#000000' : undefined}
                                    />
                                    <span className={clsx(styles.item, styles.sectionRoom, styles.units)}>
                                        {roomUnits}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const collapsed = (
        <div
            className={clsx(styles.border, styles.collapsedContainer, [styles.optionNotSelected])}
            onClick={() => setIsCollapsed(false)}
        >
            {header}
        </div>
    );

    const expanded = (
        <div
            className={clsx(styles.border, styles.expandedContainer)}
            onClick={() => setIsCollapsed(true)}
        >
            {header}
            {list}
        </div>
    );

    return (
        <ClickAwayListener onClickAway={() => setIsCollapsed(true)}>
            <div className={clsx(styles.container, props.className)}>
                <div
                    className={styles.dropdownContainer}
                    id="booking_list_rooms"
                >
                    {isCollapsed ? collapsed : expanded}
                </div>
                {isRoomSelectedFull(props.selectedOption) && (
                    <AlertNewBooking
                        className={styles.alert}
                        bWidth="2px"
                        icon={faExclamationTriangle}
                        iconSize="lg"
                        iconColor="#EAB464"
                        bgColor="#F8F5F1"
                        bdColor="#BCB8AE"
                    >
                        <span className={styles.alertText}>
                            <span className={styles.bold}>{props.selectedOption?.name}</span> is full, but you can still
                            select it.
                        </span>
                    </AlertNewBooking>
                )}
                {isRoomBySizeMismatch(props.selectedOption) && (
                    <AlertNewBooking
                        className={styles.alert}
                        bWidth="2px"
                        icon={faExclamationTriangle}
                        iconSize="lg"
                        iconColor="#EAB464"
                        bgColor="#F8F5F1"
                        bdColor="#BCB8AE"
                    >
                        <span className={styles.alertText}>{SIZE_MISMATCH_WARNING_TEXT}</span>
                    </AlertNewBooking>
                )}
            </div>
        </ClickAwayListener>
    );
};

export default SelectRoomServices;
